import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ViewerSettings } from "../../types/settings";

type LanguageCode = ViewerSettings["language"];

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  text: string;
}

interface SupportChatbotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  language?: LanguageCode;
  title?: string;
  subtitle?: string;
  titleIcon?: string;
  accentColor?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  fontFamily?: string;
}

const CHATBOT_COPY: Record<
  LanguageCode,
  {
    title: string;
    subtitle: string;
    backToSupport: string;
    closeChatbot: string;
    placeholder: string;
    send: string;
    welcome: string;
    prompts: string[];
    keywords: Record<"rotate" | "load" | "inspect" | "docs" | "zoom", string[]>;
    replies: Record<
      "rotate" | "load" | "inspect" | "docs" | "zoom" | "fallback",
      string
    >;
  }
> = {
  en: {
    title: "Support Chatbot",
    subtitle: "Ask a question and get instant guidance",
    backToSupport: "Back to support",
    closeChatbot: "Close support chatbot",
    placeholder: "Type your question...",
    send: "Send",
    welcome:
      "Hi. I can help with setup, navigation controls, part lookup, and troubleshooting. Ask me a question to get started.",
    prompts: [
      "How do I rotate the model?",
      "Why is my model not loading?",
      "How do I inspect a part?",
      "Where can I find documentation?",
    ],

    keywords: {
      rotate: ["rotate", "rotation", "orbit"],
      load: ["load", "loading", "not loading"],
      inspect: ["inspect", "part", "component"],
      docs: ["documentation", "docs", "manual"],
      zoom: ["zoom", "pinch"],
    },
    replies: {
      rotate:
        "To rotate the model, drag with one finger on touch devices. If the model is difficult to inspect, combine rotation with zoom for a closer angle.",
      load: "If the model is not loading, first confirm the selected configuration exists and the model asset path is valid. If the issue continues, try reopening the viewer or checking the network response for the model file.",
      inspect:
        "To inspect a part, rotate or pan until it is visible, then tap it once. The viewer should highlight the part and display contextual details.",
      docs: "You can open product documentation from the support menu. It contains setup steps, troubleshooting guidance, and reference material for the current product.",
      zoom: "Use two fingers to pinch outward to zoom in and pinch inward to zoom out. Smooth, even motion gives the best control.",
      fallback:
        "I can help with model controls, part inspection, loading issues, and documentation. Try asking about rotation, zoom, troubleshooting, or part information.",
    },
  },
  es: {
    title: "Chatbot de soporte",
    subtitle: "Haz una pregunta y recibe orientaci\u00f3n al instante",
    backToSupport: "Volver al soporte",
    closeChatbot: "Cerrar chatbot de soporte",
    placeholder: "Escribe tu pregunta...",
    send: "Enviar",
    welcome:
      "Hola. Puedo ayudarte con la configuraci\u00f3n, los controles de navegaci\u00f3n, la b\u00fasqueda de piezas y la soluci\u00f3n de problemas. Haz una pregunta para empezar.",
    prompts: [
      "\u00bfC\u00f3mo giro el modelo?",
      "\u00bfPor qu\u00e9 no carga mi modelo?",
      "\u00bfC\u00f3mo inspecciono una pieza?",
      "\u00bfD\u00f3nde encuentro la documentaci\u00f3n?",
    ],

    keywords: {
      rotate: ["girar", "giro", "rotar", "rotaci\u00f3n"],
      load: ["carga", "cargar", "cargando", "no carga"],
      inspect: ["inspeccionar", "pieza", "componente"],
      docs: ["documentaci\u00f3n", "manual", "docs"],
      zoom: ["zoom", "pellizcar"],
    },
    replies: {
      rotate:
        "Para girar el modelo, arrastra con un dedo en dispositivos t\u00e1ctiles. Si es dif\u00edcil inspeccionarlo, combina la rotaci\u00f3n con el zoom para obtener un \u00e1ngulo m\u00e1s cercano.",
      load: "Si el modelo no carga, primero confirma que la configuraci\u00f3n seleccionada exista y que la ruta del recurso del modelo sea v\u00e1lida. Si el problema contin\u00faa, vuelve a abrir el visor o revisa la respuesta de red del archivo del modelo.",
      inspect:
        "Para inspeccionar una pieza, g\u00edrala o despl\u00e1zala hasta que sea visible y luego t\u00f3cala una vez. El visor deber\u00eda resaltar la pieza y mostrar detalles contextuales.",
      docs: "Puedes abrir la documentaci\u00f3n del producto desde el men\u00fa de soporte. Contiene pasos de configuraci\u00f3n, ayuda para solucionar problemas y material de referencia del producto actual.",
      zoom: "Usa dos dedos para separar y acercar en la pantalla para hacer zoom. Un movimiento suave y uniforme ofrece el mejor control.",
      fallback:
        "Puedo ayudarte con los controles del modelo, la inspecci\u00f3n de piezas, los problemas de carga y la documentaci\u00f3n. Prueba a preguntar sobre giro, zoom, soluci\u00f3n de problemas o informaci\u00f3n de piezas.",
    },
  },
  fr: {
    title: "Chatbot de support",
    subtitle: "Posez une question et obtenez une aide imm\u00e9diate",
    backToSupport: "Retour au support",
    closeChatbot: "Fermer le chatbot de support",
    placeholder: "Saisissez votre question...",
    send: "Envoyer",
    welcome:
      "Bonjour. Je peux vous aider pour la configuration, les commandes de navigation, la recherche de pi\u00e8ces et le d\u00e9pannage. Posez une question pour commencer.",
    prompts: [
      "Comment faire pivoter le mod\u00e8le ?",
      "Pourquoi mon mod\u00e8le ne se charge-t-il pas ?",
      "Comment inspecter une pi\u00e8ce ?",
      "O\u00f9 trouver la documentation ?",
    ],

    keywords: {
      rotate: ["pivoter", "rotation", "tourner"],
      load: ["charger", "chargement", "ne se charge pas"],
      inspect: ["inspecter", "pi\u00e8ce", "composant"],
      docs: ["documentation", "manuel", "docs"],
      zoom: ["zoom", "pincer"],
    },
    replies: {
      rotate:
        "Pour faire pivoter le mod\u00e8le, faites glisser un doigt sur les appareils tactiles. Si le mod\u00e8le est difficile \u00e0 inspecter, combinez la rotation avec le zoom pour un angle plus rapproch\u00e9.",
      load: "Si le mod\u00e8le ne se charge pas, v\u00e9rifiez d\u2019abord que la configuration s\u00e9lectionn\u00e9e existe et que le chemin de la ressource du mod\u00e8le est valide. Si le probl\u00e8me persiste, rouvrez le visualiseur ou contr\u00f4lez la r\u00e9ponse r\u00e9seau du fichier du mod\u00e8le.",
      inspect:
        "Pour inspecter une pi\u00e8ce, faites pivoter ou d\u00e9placez le mod\u00e8le jusqu\u2019\u00e0 ce qu\u2019elle soit visible, puis touchez-la une fois. Le visualiseur doit la mettre en surbrillance et afficher des d\u00e9tails contextuels.",
      docs: "Vous pouvez ouvrir la documentation produit depuis le menu de support. Elle contient des \u00e9tapes d\u2019installation, des conseils de d\u00e9pannage et des documents de r\u00e9f\u00e9rence pour le produit actuel.",
      zoom: "Utilisez deux doigts pour pincer vers l\u2019ext\u00e9rieur afin de zoomer et vers l\u2019int\u00e9rieur pour d\u00e9zoomer. Un geste fluide offre le meilleur contr\u00f4le.",
      fallback:
        "Je peux vous aider avec les commandes du mod\u00e8le, l\u2019inspection des pi\u00e8ces, les probl\u00e8mes de chargement et la documentation. Essayez de poser une question sur la rotation, le zoom, le d\u00e9pannage ou les pi\u00e8ces.",
    },
  },
  de: {
    title: "Support-Chatbot",
    subtitle: "Stellen Sie eine Frage und erhalten Sie sofort Hilfe",
    backToSupport: "Zur\u00fcck zum Support",
    closeChatbot: "Support-Chatbot schlie\u00dfen",
    placeholder: "Geben Sie Ihre Frage ein...",
    send: "Senden",
    welcome:
      "Hallo. Ich kann bei Einrichtung, Navigationssteuerung, Teile-Suche und Fehlerbehebung helfen. Stellen Sie eine Frage, um zu beginnen.",
    prompts: [
      "Wie drehe ich das Modell?",
      "Warum wird mein Modell nicht geladen?",
      "Wie inspiziere ich ein Teil?",
      "Wo finde ich die Dokumentation?",
    ],

    keywords: {
      rotate: ["drehen", "rotation", "rotieren"],
      load: ["laden", "l\u00e4dt", "geladen", "nicht geladen"],
      inspect: ["inspizieren", "teil", "komponente"],
      docs: ["dokumentation", "handbuch", "docs"],
      zoom: ["zoom", "ziehen"],
    },
    replies: {
      rotate:
        "Um das Modell zu drehen, ziehen Sie auf Touch-Ger\u00e4ten mit einem Finger. Wenn das Modell schwer zu inspizieren ist, kombinieren Sie die Drehung mit Zoom f\u00fcr eine n\u00e4here Ansicht.",
      load: "Wenn das Modell nicht geladen wird, pr\u00fcfen Sie zuerst, ob die ausgew\u00e4hlte Konfiguration existiert und der Pfad zur Modelldatei g\u00fcltig ist. Wenn das Problem weiterhin besteht, \u00f6ffnen Sie den Viewer erneut oder pr\u00fcfen Sie die Netzwerkantwort der Modelldatei.",
      inspect:
        "Um ein Teil zu inspizieren, drehen oder verschieben Sie das Modell, bis es sichtbar ist, und tippen Sie dann einmal darauf. Der Viewer sollte das Teil hervorheben und kontextbezogene Details anzeigen.",
      docs: "Sie k\u00f6nnen die Produktdokumentation \u00fcber das Support-Men\u00fc \u00f6ffnen. Sie enth\u00e4lt Einrichtungsschritte, Hinweise zur Fehlerbehebung und Referenzmaterial f\u00fcr das aktuelle Produkt.",
      zoom: "Verwenden Sie zwei Finger, um nach au\u00dfen zu ziehen und hineinzuzoomen, oder nach innen, um herauszuzoomen. Eine gleichm\u00e4\u00dfige Bewegung bietet die beste Kontrolle.",
      fallback:
        "Ich kann bei Modellsteuerung, Teile-Inspektion, Ladeproblemen und Dokumentation helfen. Fragen Sie nach Drehung, Zoom, Fehlerbehebung oder Teileinformationen.",
    },
  },
  zh: {
    title: "\u652f\u6301\u804a\u5929\u673a\u5668\u4eba",
    subtitle:
      "\u63d0\u51fa\u95ee\u9898\uff0c\u7acb\u5373\u83b7\u53d6\u6307\u5f15",
    backToSupport: "\u8fd4\u56de\u652f\u6301",
    closeChatbot: "\u5173\u95ed\u652f\u6301\u804a\u5929\u673a\u5668\u4eba",
    placeholder: "\u8f93\u5165\u60a8\u7684\u95ee\u9898...",
    send: "\u53d1\u9001",
    welcome:
      "\u4f60\u597d\u3002\u6211\u53ef\u4ee5\u5e2e\u52a9\u5904\u7406\u8bbe\u7f6e\u3001\u5bfc\u822a\u63a7\u4ef6\u3001\u90e8\u4ef6\u67e5\u627e\u548c\u6545\u969c\u6392\u9664\u3002\u8bf7\u5148\u63d0\u51fa\u4e00\u4e2a\u95ee\u9898\u3002",
    prompts: [
      "\u5982\u4f55\u65cb\u8f6c\u6a21\u578b\uff1f",
      "\u4e3a\u4ec0\u4e48\u6a21\u578b\u6ca1\u6709\u52a0\u8f7d\uff1f",
      "\u5982\u4f55\u67e5\u770b\u90e8\u4ef6\uff1f",
      "\u5728\u54ea\u91cc\u53ef\u4ee5\u627e\u5230\u6587\u6863\uff1f",
    ],

    keywords: {
      rotate: ["\u65cb\u8f6c", "\u8f6c\u52a8"],
      load: ["\u52a0\u8f7d", "\u672a\u52a0\u8f7d"],
      inspect: ["\u67e5\u770b", "\u68c0\u67e5", "\u90e8\u4ef6"],
      docs: ["\u6587\u6863", "\u624b\u518c", "\u8bf4\u660e"],
      zoom: ["\u7f29\u653e", "\u53cc\u6307"],
    },
    replies: {
      rotate:
        "\u8981\u65cb\u8f6c\u6a21\u578b\uff0c\u8bf7\u5728\u89e6\u6478\u8bbe\u5907\u4e0a\u7528\u4e00\u6839\u624b\u6307\u62d6\u52a8\u3002\u5982\u679c\u6a21\u578b\u4e0d\u6613\u67e5\u770b\uff0c\u53ef\u4ee5\u5c06\u65cb\u8f6c\u4e0e\u7f29\u653e\u7ed3\u5408\u4f7f\u7528\uff0c\u4ee5\u83b7\u5f97\u66f4\u8fd1\u7684\u89d2\u5ea6\u3002",
      load: "\u5982\u679c\u6a21\u578b\u672a\u80fd\u52a0\u8f7d\uff0c\u8bf7\u5148\u786e\u8ba4\u6240\u9009\u914d\u7f6e\u5b58\u5728\uff0c\u4e14\u6a21\u578b\u8d44\u6e90\u8def\u5f84\u6709\u6548\u3002\u82e5\u95ee\u9898\u4ecd\u7136\u5b58\u5728\uff0c\u8bf7\u91cd\u65b0\u6253\u5f00\u67e5\u770b\u5668\uff0c\u6216\u68c0\u67e5\u6a21\u578b\u6587\u4ef6\u7684\u7f51\u7edc\u54cd\u5e94\u3002",
      inspect:
        "\u8981\u67e5\u770b\u90e8\u4ef6\uff0c\u8bf7\u5148\u65cb\u8f6c\u6216\u5e73\u79fb\u6a21\u578b\uff0c\u76f4\u5230\u5b83\u53ef\u89c1\uff0c\u7136\u540e\u70b9\u51fb\u4e00\u6b21\u3002\u67e5\u770b\u5668\u5e94\u4f1a\u9ad8\u4eae\u8be5\u90e8\u4ef6\u5e76\u663e\u793a\u76f8\u5173\u8be6\u60c5\u3002",
      docs: "\u4f60\u53ef\u4ee5\u4ece\u652f\u6301\u83dc\u5355\u6253\u5f00\u4ea7\u54c1\u6587\u6863\u3002\u5176\u4e2d\u5305\u542b\u8bbe\u7f6e\u6b65\u9aa4\u3001\u6545\u969c\u6392\u9664\u6307\u5357\u548c\u5f53\u524d\u4ea7\u54c1\u7684\u53c2\u8003\u8d44\u6599\u3002",
      zoom: "\u4f7f\u7528\u4e24\u6839\u624b\u6307\u5411\u5916\u634f\u5408\u53ef\u653e\u5927\uff0c\u5411\u5185\u634f\u5408\u53ef\u7f29\u5c0f\u3002\u4fdd\u6301\u52a8\u4f5c\u5e73\u7a33\u53ef\u83b7\u5f97\u6700\u4f73\u63a7\u5236\u6548\u679c\u3002",
      fallback:
        "\u6211\u53ef\u4ee5\u5e2e\u52a9\u5904\u7406\u6a21\u578b\u63a7\u5236\u3001\u90e8\u4ef6\u67e5\u770b\u3001\u52a0\u8f7d\u95ee\u9898\u548c\u6587\u6863\u67e5\u9605\u3002\u4f60\u53ef\u4ee5\u8be2\u95ee\u65cb\u8f6c\u3001\u7f29\u653e\u3001\u6545\u969c\u6392\u9664\u6216\u90e8\u4ef6\u4fe1\u606f\u3002",
    },
  },
};

function createInitialMessages(
  copy: (typeof CHATBOT_COPY)[LanguageCode],
): ChatMessage[] {
  return [
    {
      id: "assistant-welcome",
      role: "assistant",
      text: copy.welcome,
    },
  ];
}

function includesAny(input: string, keywords: string[]) {
  return keywords.some((keyword) => input.includes(keyword));
}

function buildAssistantReply(input: string, language: LanguageCode) {
  const copy = CHATBOT_COPY[language] ?? CHATBOT_COPY.en;
  const normalized = input.toLowerCase();

  if (includesAny(normalized, copy.keywords.rotate)) {
    return copy.replies.rotate;
  }

  if (includesAny(normalized, copy.keywords.load)) {
    return copy.replies.load;
  }

  if (includesAny(normalized, copy.keywords.inspect)) {
    return copy.replies.inspect;
  }

  if (includesAny(normalized, copy.keywords.docs)) {
    return copy.replies.docs;
  }

  if (includesAny(normalized, copy.keywords.zoom)) {
    return copy.replies.zoom;
  }

  return copy.replies.fallback;
}

export function SupportChatbotModal({
  isOpen,
  onClose,
  onBack,
  language = "en",
  title,
  subtitle,
  titleIcon = "smart_toy",
  accentColor = "rgb(56, 189, 248)",
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  fontFamily = "Arial, sans-serif",
}: SupportChatbotModalProps) {
  const copy = CHATBOT_COPY[language] ?? CHATBOT_COPY.en;
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    createInitialMessages(copy),
  );
  const [inputValue, setInputValue] = useState("");
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setMessages((previous) => {
      if (previous.length !== 1 || previous[0]?.id !== "assistant-welcome") {
        return previous;
      }

      return createInitialMessages(copy);
    });
  }, [copy]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const openTimer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(openTimer);
    }

    setIsVisible(false);
    const closeTimer = window.setTimeout(() => setShouldRender(false), 300);
    return () => window.clearTimeout(closeTimer);
  }, [isOpen]);

  const canSend = useMemo(() => inputValue.trim().length > 0, [inputValue]);

  const sendMessage = (text: string) => {
    const value = text.trim();
    if (!value) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: value,
    };

    const assistantMessage: ChatMessage = {
      id: `assistant-${Date.now() + 1}`,
      role: "assistant",
      text: buildAssistantReply(value, language),
    };

    setMessages((previous) => [...previous, userMessage, assistantMessage]);
    setInputValue("");
    setSelectedSuggestion(null);
  };

  if (!shouldRender) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 240,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 17, 21, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        fontFamily,
        padding: "1.5rem",
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-chatbot-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(600px, 90vw)",
          maxHeight: "72vh",
          borderRadius: "2rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: "transform 0.3s ease-in-out",
        }}
      >
        <button
          onClick={onBack ?? onClose}
          aria-label={copy.backToSupport}
          style={{
            position: "absolute",
            top: "1.5rem",
            left: "1.5rem",
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: "transparent",
            border: "none",
            color: secondaryTextColor,
            cursor: "pointer",
            transition: "all 0.3s",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = secondaryTextColor;
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "1.25rem", fontWeight: 300 }}
          >
            arrow_back
          </span>
        </button>

        <button
          onClick={onClose}
          aria-label={copy.closeChatbot}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: "transparent",
            border: "none",
            color: secondaryTextColor,
            cursor: "pointer",
            transition: "all 0.3s",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = secondaryTextColor;
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "1.25rem", fontWeight: 300 }}
          >
            close
          </span>
        </button>

        <div style={{ padding: "1.5rem 2rem 1rem 2rem", textAlign: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              marginBottom: "0.25rem",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                color: accentColor,
                fontSize: "1.25rem",
                fontWeight: 300,
              }}
            >
              {titleIcon}
            </span>
            <h2
              id="support-chatbot-title"
              style={{
                margin: 0,
                fontSize: "1.1rem",
                fontWeight: 700,
                letterSpacing: "0.025em",
                color: primaryTextColor,
              }}
            >
              {title ?? copy.title}
            </h2>
          </div>
        </div>

        <div
          style={{
            padding: "0 2rem 1rem 2rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            overflowY: "auto",
            maxHeight: "36vh",
            flex: 1,
          }}
        >
          {messages.map((message, index) => {
            const isAssistant = message.role === "assistant";
            return (
              <div key={message.id}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: isAssistant ? "flex-start" : "flex-end",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "82%",
                      padding: "0.65rem 0.9rem",
                      borderRadius: isAssistant
                        ? "1rem 1rem 1rem 0.25rem"
                        : "1rem 1rem 0.25rem 1rem",
                      background: isAssistant
                        ? `${accentColor}12`
                        : `${accentColor}22`,
                      border: `1px solid ${isAssistant ? `${accentColor}25` : `${accentColor}35`}`,
                      color: primaryTextColor,
                      fontSize: "0.75rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {message.text}
                  </div>
                </div>

                {isAssistant && index === 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      marginTop: "0.75rem",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    {copy.prompts.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() => {
                          setSelectedSuggestion(prompt);
                          sendMessage(prompt);
                        }}
                        style={{
                          padding: "0.5rem 0.9rem",
                          borderRadius: "999px",
                          border:
                            selectedSuggestion === prompt
                              ? `1.5px solid ${accentColor}`
                              : "1px solid rgba(255,255,255,0.12)",
                          background:
                            selectedSuggestion === prompt
                              ? `${accentColor}20`
                              : "rgba(255,255,255,0.04)",
                          color: primaryTextColor,
                          cursor: "pointer",
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (selectedSuggestion !== prompt) {
                            e.currentTarget.style.background =
                              "rgba(255,255,255,0.08)";
                            e.currentTarget.style.borderColor =
                              "rgba(255,255,255,0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedSuggestion !== prompt) {
                            e.currentTarget.style.background =
                              "rgba(255,255,255,0.04)";
                            e.currentTarget.style.borderColor =
                              "rgba(255,255,255,0.12)";
                          }
                        }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div
          style={{
            padding: "1rem 2rem 1.5rem 2rem",
            display: "flex",
            alignItems: "flex-end",
            gap: "0.75rem",
            borderTop: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <input
            type="text"
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyPress={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                sendMessage(inputValue);
              }
            }}
            placeholder={copy.placeholder}
            style={{
              flex: 1,
              borderRadius: "999px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.04)",
              color: primaryTextColor,
              padding: "0.7rem 1rem",
              outline: "none",
              fontSize: "0.75rem",
              lineHeight: 1.5,
              transition: "all 0.3s",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = `${accentColor}60`;
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
            }}
          />

          <button
            onClick={() => sendMessage(inputValue)}
            disabled={!canSend}
            style={{
              padding: "0.7rem 1.1rem",
              fontSize: "0.7rem",
              fontWeight: 700,
              borderRadius: "999px",
              background: accentColor,
              color: "white",
              border: "none",
              cursor: canSend ? "pointer" : "not-allowed",
              boxShadow: `0 0 20px ${accentColor}4D`,
              transition: "all 0.3s",
              opacity: canSend ? 1 : 0.5,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.3rem",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              if (!canSend) return;
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 2px 24px ${accentColor}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 0 20px ${accentColor}4D`;
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "0.9rem", fontWeight: 300 }}
            >
              send
            </span>
            {copy.send}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
